import { useReactFlow } from "@xyflow/react";
import { useEffect } from "react";
import type { FC, RefObject } from "react";

/** Legacy canvas opens at 100% zoom with the stack centered horizontally and
 *  30px from the top (EditAutomationSidebarContentStyles top padding parity).
 *  Re-runs when `viewportResetKey` bumps (AutomationForm calls
 *  `flowCanvas.reset()` on open), so zoom returns to 100% even if the canvas
 *  stayed mounted between modal opens.
 *
 *  The graph is laid out symmetrically around flow-x=0 (layout.ts centers
 *  every level on that axis), so "centered" == flow-x=0 sitting at the shell's
 *  horizontal midpoint. */
export const FlowViewportInit: FC<{
  shellRef: RefObject<HTMLDivElement>;
  ready: boolean;
  viewportResetKey: number;
}> = ({ shellRef, ready, viewportResetKey }) => {
  const { setViewport, getViewport } = useReactFlow();

  useEffect(() => {
    if (!ready) return;
    const shell = shellRef.current;
    if (shell === null) return;

    void setViewport(
      { x: shell.clientWidth / 2, y: 30, zoom: 1 },
      { duration: 0 },
    );
  }, [ready, viewportResetKey, setViewport, shellRef]);

  // Keep the flow horizontally centered when the canvas RESIZES. Adding a
  // Conditional Logic block opens the right settings panel and widens the tree
  // into a branch fan; without re-centering, the shell's midpoint shifts and
  // the fan (wider than the single column above it) gets clipped off the left
  // edge. Only the x axis is re-centered — the user's zoom and vertical pan
  // are preserved.
  useEffect(() => {
    if (!ready) return;
    const shell = shellRef.current;
    if (shell === null || typeof ResizeObserver === "undefined") return;

    let prevWidth = shell.clientWidth;
    const observer = new ResizeObserver(() => {
      const width = shell.clientWidth;
      if (width === 0 || width === prevWidth) return;
      prevWidth = width;
      const { zoom, y } = getViewport();
      void setViewport({ x: width / 2, y, zoom }, { duration: 0 });
    });
    observer.observe(shell);
    return () => observer.disconnect();
  }, [ready, setViewport, getViewport, shellRef]);

  return null;
};
FlowViewportInit.displayName = "FlowViewportInit";
